import React, { useState } from "react";
import I18n from "i18n-js";

import Select from "../../../../common/components/form/Select";
import { ADD_COMPONENT } from "../constants";
import StatelessTextInput from "../../../../common/components/form/StatelessTextInput";
import DateInput from "../../../../common/components/form/DateInput";
import SubmitButton from "../../../../common/components/form/SubmitButton";
import { StandaloneInvalidFeedback } from "../../../../common/components/form/InvalidFeedback";
import { clamp } from "../../../../common/utils/mathUtils";

import { v4 as uuidv4 } from "uuid";
import { func, shape } from "prop-types";

const COMPONENT_COMPRESSOR_UUID = "762eb1b3-c83b-4a40-b63d-d614b896eed8";

AddComponentForm.propTypes = {
  nomenclaturesList: shape(),
  dispatch: func,
  close: func,
};

export default function AddComponentForm({
  nomenclaturesList,
  dispatch,
  close,
}) {
  const {
    componentNatureClassifications,
    componentNatureTypes,
    componentNatures,
  } = nomenclaturesList;

  const [formFields, setFormFields] = useState({
    nature: null,
    type: null,
    classification: null,
    brand: null,
    model: null,
    serialNumber: null,
    mark: null,
    designation: null,
    barcode: null,
    commissioningDate: null,
    usagePercent: null,
  });

  const natureTypeOptions = formFields.nature
    ? componentNatureTypes?.filter((v) => v.natureUuid === formFields.nature)
    : [];
  const natureClassificationOptions = formFields.nature
    ? componentNatureClassifications.filter(
        (v) => v.natureUuid === formFields.nature
      )
    : [];
  const natureBrandOptions = formFields.nature
      ? componentNatures.find((v) => v.uuid === formFields.nature)?.brands ?? []
      : [];

  function onSubmit() {
    if (!isFormValid()) {
      return;
    }
    dispatch({
      type: ADD_COMPONENT,
      payload: {
        uuid: uuidv4(),
        nature: formFields.nature,
        natureType: formFields.type,
        natureClassification: formFields.classification,
        brand: formFields.brand,
        model: formFields.model,
        serialNumber: formFields.serialNumber,
        mark: formFields.mark,
        designation: formFields.designation,
        barcode: formFields.barcode,
        commissioningDate: formFields.commissioningDate,
        usagePercent: formFields.usagePercent,
        deleteable: true,
      },
    });
    close();
  }

  const [errors, setErrors] = useState({});

  function isFormValid() {
    const fields = ["nature"];

    if (natureTypeOptions.length !== 0) fields.push("type");
    if (natureClassificationOptions.length !== 0) fields.push("classification");

    let errors = {};
    let isValid = true;

    fields.forEach((field) => {
      if (formFields[field] == null || formFields[field] === "") {
        errors[field] = "This field is required";
        isValid = false;
      }
    });
    if (isValid == false)
      errors["global"] = "Some fields are not filled correctly";
    setErrors(errors);

    return isValid;
  }

  return (
    <div style={{ marginLeft: "10px", marginRight: "10px" }}>
      <h2 style={{ textAlign: "center" }}>
        {I18n.t("pages.installations.view.components.form:title")}
      </h2>
      <Select
        id="componentNature"
        onChange={(event) => {
          setFormFields({
            ...formFields,
            nature: event.target.value,
            type: null,
            classification: null,
            usagePercent: null,
          });
        }}
        label={I18n.t("pages.installations.view.components.nature:label")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {option.designation}
          </option>
        )}
        options={componentNatures ?? []}
        placeholder={I18n.t("pages.installations.view.components.nature:label")}
        error={errors.nature}
      />
      <Select
        id="componentClassification"
        onChange={(event) => {
          setFormFields({ ...formFields, classification: event.target.value });
        }}
        label={I18n.t("pages.installations.view.components.category:label")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {option.designation}
          </option>
        )}
        options={natureClassificationOptions}
        placeholder={I18n.t(
          "pages.installations.view.components.category:label"
        )}
        disabled={natureClassificationOptions.length == 0}
        error={errors.classification}
        optional={natureClassificationOptions.length == 0}
      />
      <Select
        id="componentType"
        onChange={(event) => {
          setFormFields({ ...formFields, type: event.target.value });
        }}
        label={I18n.t("pages.installations.view.components.type:label")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {option.designation}
          </option>
        )}
        options={natureTypeOptions}
        placeholder={I18n.t("pages.installations.view.components.type:label")}
        disabled={natureTypeOptions.length == 0}
        error={errors.type}
        optional={natureTypeOptions.length == 0}
      />
      <Select
        id="componentBrand"
        onChange={(event) => {
          setFormFields({ ...formFields, brand: event.target.value });
        }}
        label={I18n.t("pages.installations.view.components.brand:label")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {option.designation}
          </option>
        )}
        options={natureBrandOptions}
        placeholder={I18n.t("pages.installations.view.components.brand:label")}
        disabled={natureBrandOptions.length == 0}
        error={errors.brand}
        optional
      />
      <StatelessTextInput
        id={"model"}
        onChangedValue={(value) =>
          setFormFields({ ...formFields, model: value })
        }
        label={I18n.t("pages.installations.view.components.model:label")}
        errors={errors.model}
        optional
      />
      <StatelessTextInput
        id={"serialNumber"}
        onChangedValue={(value) =>
          setFormFields({ ...formFields, serialNumber: value })
        }
        label={I18n.t(
          "pages.installations.view.components.serial-number:label"
        )}
        errors={errors.serialNumber}
        optional
      />
      <StatelessTextInput
        id={"mark"}
        onChangedValue={(value) =>
          setFormFields({ ...formFields, mark: value })
        }
        label={I18n.t("pages.installations.view.components.mark:label")}
        errors={errors.mark}
        optional
      />
      <StatelessTextInput
        id={"designation"}
        onChangedValue={(value) =>
          setFormFields({ ...formFields, designation: value })
        }
        label={I18n.t("pages.installations.view.components.designation:label")}
        errors={errors.designation}
        optional
      />
      <StatelessTextInput
        id={"barcode"}
        onChangedValue={(value) =>
          setFormFields({ ...formFields, barcode: value })
        }
        label={I18n.t("pages.installations.view.components.barcode:label")}
        errors={errors.barcode}
        optional
      />
      <DateInput
        id="commissioningDate"
        label={I18n.t(
          "pages.installations.view.components.commissioning-date:label"
        )}
        onChange={(newDate) =>
          setFormFields({ ...formFields, commissioningDate: newDate })
        }
        error={errors.commissioningDate}
        optional
      />
      <StatelessTextInput
        id={"usagePercent"}
        type="number"
        value={formFields.usagePercent ?? ""}
        onChangedValue={(value) => {
          // Ensure percent value is between 0 and 100
          setFormFields({
            ...formFields,
            usagePercent: clamp(value, 0, 100),
          });
        }}
        label={I18n.t(
          "pages.installations.view.components.usage-percent:label"
        )}
        disabled={formFields.nature !== COMPONENT_COMPRESSOR_UUID}
        errors={errors.usagePercent}
        optional
      />
      <StandaloneInvalidFeedback
        errors={errors.global}
        classes={"invalid-feedback-right"}
      />
      <SubmitButton loading={false} onClick={onSubmit} />
    </div>
  );
}
